import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { CHANNEL_META, charCountColor } from '../../utils/helpers';
import { aiService } from '../../services/aiService';

export default function ContentPiece({ piece }) {
  const { updateContentPiece, removeContentPiece, voiceState, anthropicKey, anthropicKeyReady, ideas, logActivity } = useApp();
  const [expanded,     setExpanded]     = useState(false);
  const [editing,      setEditing]      = useState(false);
  const [editText,     setEditText]     = useState(piece.content || '');
  const [regenerating, setRegenerating] = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [saving,       setSaving]       = useState(false);

  const meta      = CHANNEL_META[piece.channel] || {};
  const chars     = (piece.content || '').length;
  const countClass= charCountColor(chars, meta.maxChars);

  async function saveEdit() {
    setSaving(true);
    try {
      await updateContentPiece(piece.id, { content: editText });
      setEditing(false);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function approve() {
    await updateContentPiece(piece.id, { status:'approved' }).catch(console.error);
    logActivity('Approved content', `${meta.label} for "${piece.idea_title}"`);
  }

  function copy() {
    navigator.clipboard.writeText(piece.content || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function regenerate() {
    if (!anthropicKeyReady) { alert('Add Anthropic key in Settings → API Keys.'); return; }
    setRegenerating(true);
    try {
      const idea = ideas.find(i => i.id === piece.idea_id);
      const text = await aiService.generateContent({
        voiceProfile: voiceState,
        idea,
        channels:    [piece.channel],
        anthropicKey,
      });
      await updateContentPiece(piece.id, { content: text, status:'draft' });
    } catch (err) {
      alert('Regenerate error: ' + err.message);
    } finally {
      setRegenerating(false);
    }
  }

  async function remove() {
    if (!confirm('Remove this piece?')) return;
    await removeContentPiece(piece.id).catch(console.error);
  }

  const statusStyle = {
    draft:     'gs-draft',
    approved:  'gs-approved',
    scheduled: 'gs-scheduled',
    published: 'gs-approved',
    archived:  'gs-draft',
  }[piece.status] || 'gs-draft';

  return (
    <div className={`gen-piece ${piece.status === 'approved' ? 'approved' : ''}`}>
      <div className="gp-head">
        <div className="gp-ch-dot" style={{ background:meta.color }} />
        <div className="gp-ch-name">{meta.emoji} {meta.label}</div>
        {meta.maxChars && <div className="gp-ch-fmt">{meta.maxChars} chars max</div>}
        <span className={`gp-status ${statusStyle}`}>{piece.status}</span>
      </div>

      <div className="gp-body">
        {editing ? (
          <>
            <textarea className="f-ta" rows={8} value={editText}
              onChange={e => setEditText(e.target.value)} autoFocus />
            <div style={{ display:'flex', gap:6, marginTop:6 }}>
              <button className="btn btn-primary btn-xs" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-outline btn-xs" onClick={() => { setEditing(false); setEditText(piece.content || ''); }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={`gp-text ${expanded ? 'expanded' : ''}`}
              style={{ cursor:'pointer' }}
              onClick={() => setExpanded(e => !e)}>
              {piece.content || <span style={{ color:'#9ca3af' }}>No content generated.</span>}
            </div>
            {meta.maxChars && (
              <div className={`gp-chars ${countClass}`}>{chars} / {meta.maxChars}</div>
            )}
          </>
        )}
      </div>

      <div className="gp-foot">
        {piece.status === 'draft' && (
          <button className="btn btn-green btn-xs" onClick={approve}>✓ Approve</button>
        )}
        <button className="btn btn-outline btn-xs" onClick={() => { setEditing(true); setEditText(piece.content || ''); }}>
          ✏️ Edit
        </button>
        <button className="btn btn-outline btn-xs" onClick={copy}>{copied ? '✓ Copied!' : '📋 Copy'}</button>
        <button className="btn btn-ghost btn-xs" onClick={regenerate} disabled={regenerating}>
          {regenerating ? <span className="dots"><i/><i/><i/></span> : '🔄 Regen'}
        </button>
        <button className="btn btn-danger btn-xs" onClick={remove}>✕</button>
      </div>
    </div>
  );
}
